import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { AdminHeader, Modal, ConfirmDelete, EditBtn, AddBtn, Field } from "../../components/admin/AdminUI";

const empty = { title: "", category: "General", image: "", summary: "", description: "", tags: [], seo_title: "", seo_description: "", publish_date: new Date().toISOString().slice(0, 10), featured: false, status: "published" };

export default function AnnouncementsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
      if (editing) await api.put(`/admin/announcements/${editing}`, payload);
      else await api.post("/admin/announcements", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const edit = (a) => {
    setEditing(a.id);
    setForm({ ...empty, ...a, tags: (a.tags || []).join(", "), publish_date: (a.publish_date || "").slice(0, 10) });
    setOpen(true);
  };
  const del = async (id) => { await api.delete(`/admin/announcements/${id}`); load(); toast.success("Deleted"); };

  return (
    <div>
      <AdminHeader title="Announcements" subtitle="Manage news, notices and communications" actions={<AddBtn onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} />} />
      <div className="card-elev overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg text-brand-muted text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left">Category</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Featured</th>
              <th className="px-4 py-3 text-left">Views</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border">
            {items.map((a) => (
              <tr key={a.id} className="hover:bg-brand-bg/50">
                <td className="px-4 py-3">
                  <div className="font-semibold text-brand-text">{a.title}</div>
                  <div className="text-xs text-brand-muted">{new Date(a.publish_date).toLocaleDateString()}</div>
                </td>
                <td className="px-4 py-3">{a.category}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${a.status === "published" ? "bg-brand-accent/10 text-brand-accent" : "bg-slate-100 text-slate-600"}`}>{a.status}</span></td>
                <td className="px-4 py-3">{a.featured ? "Yes" : "No"}</td>
                <td className="px-4 py-3">{a.views}</td>
                <td className="px-4 py-3 text-right"><div className="inline-flex items-center gap-1"><EditBtn onClick={() => edit(a)} /><ConfirmDelete onConfirm={() => del(a.id)} /></div></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-brand-muted">No announcements yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Announcement" : "New Announcement"} size="lg">
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Title *"><input required value={form.title} onChange={upd("title")} className="form-input" data-testid="ann-title" /></Field>
          <Field label="Category"><input value={form.category} onChange={upd("category")} className="form-input" /></Field>
          <Field label="Image URL"><input value={form.image} onChange={upd("image")} className="form-input" placeholder="https://…" /></Field>
          <Field label="Publish Date"><input type="date" value={form.publish_date} onChange={upd("publish_date")} className="form-input" /></Field>
          <div className="md:col-span-2"><Field label="Summary"><textarea rows={2} value={form.summary} onChange={upd("summary")} className="form-input" /></Field></div>
          <div className="md:col-span-2"><Field label="Description"><textarea rows={6} value={form.description} onChange={upd("description")} className="form-input" /></Field></div>
          <Field label="Tags (comma separated)"><input value={form.tags} onChange={upd("tags")} className="form-input" /></Field>
          <Field label="Status"><select value={form.status} onChange={upd("status")} className="form-input"><option value="published">Published</option><option value="draft">Draft</option></select></Field>
          <Field label="SEO Title"><input value={form.seo_title} onChange={upd("seo_title")} className="form-input" /></Field>
          <Field label="SEO Description"><input value={form.seo_description} onChange={upd("seo_description")} className="form-input" /></Field>
          <label className="flex items-center gap-2 md:col-span-2"><input type="checkbox" checked={form.featured} onChange={upd("featured")} /> <span className="text-sm">Featured on homepage</span></label>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2"><button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button><button className="btn-primary" data-testid="ann-save">{editing ? "Update" : "Create"}</button></div>
        </form>
      </Modal>
    </div>
  );
}
